// Source Catalog — every data pipeline this dashboard knows about.
// Status is what an engineer needs to ship the layer:
//   live      — adapter wired, endpoint working, no key required (or key shipped via env)
//   ready     — adapter scaffolded; flip a switch / drop a key to go live
//   planned   — known endpoint, no adapter yet; one afternoon of work
//   research  — we know the data exists but not the public endpoint
//
// Categories drive the "Source Catalog" panel groupings.

export type SourceStatus = "live" | "ready" | "planned" | "research";

export type SourceCategory =
  | "mobility"      // traffic, transit, shuttle
  | "incidents"     // citizen reports, traffic events
  | "environment"   // weather, air, water, vegetation
  | "imagery"       // satellite, aerial, CCTV
  | "vibes"         // news, social, search trends
  | "infrastructure" // utilities, parking, energy
  | "campus";       // chula-specific (shuttle, parking, security)

export interface SourceEntry {
  id: string;
  label: string;
  vendor: string;
  category: SourceCategory;
  status: SourceStatus;
  endpoint?: string;     // URL or pattern
  apiPath?: string;      // path served by our API (when live)
  keyEnv?: string;       // env var name if a key is required
  pollSeconds?: number;
  describe: string;      // 1-line, engineer-readable
  docs?: string;
}

export const SOURCE_CATALOG: SourceEntry[] = [
  // ---- Mobility ----
  {
    id: "itic-longdo",
    label: "iTIC / Longdo traffic events",
    vendor: "iTIC + Longdo Map",
    category: "mobility",
    status: "live",
    endpoint: "https://event.longdo.com/feed/json",
    apiPath: "/api/incidents/itic",
    pollSeconds: 180,
    describe: "Accidents, closures, construction, congestion events with lat/lng + severity.",
    docs: "https://map.longdo.com/products/traffic",
  },
  {
    id: "longdo-cameras",
    label: "Longdo CCTV cameras",
    vendor: "Longdo Map",
    category: "imagery",
    status: "ready",
    endpoint: "https://camera.longdo.com/feed/?command=json",
    apiPath: "/api/cctv/longdo",
    pollSeconds: 300,
    describe: "Hundreds of street CCTV with JPG/HLS streams; filter to Chula bbox for ~25 cameras.",
    docs: "https://camera.longdo.com",
  },
  {
    id: "bma-cctv",
    label: "BMA Traffic CCTV",
    vendor: "Bangkok Metropolitan Authority",
    category: "imagery",
    status: "research",
    endpoint: "https://www.bmatraffic.com/CCTV.aspx",
    describe: "BMA-operated traffic cameras; scrape catalog page or use ArcGIS feature service when located.",
    docs: "https://www.bmatraffic.com",
  },
  {
    id: "exat-cctv",
    label: "EXAT expressway cameras",
    vendor: "Expressway Authority of Thailand",
    category: "imagery",
    status: "research",
    endpoint: "https://www.exat.co.th/cctv/",
    describe: "Expressway cams; Rama IX / Sirat / Chalerm Maha Nakhon segments visible from Rama IV.",
  },
  {
    id: "highways-th",
    label: "Department of Highways cameras",
    vendor: "Thai Department of Highways",
    category: "imagery",
    status: "research",
    endpoint: "https://highway.dxc-thailand.com/",
    describe: "Highway 1/3/35 cameras; useful only if we extend the bbox out to expressway entrances.",
  },
  {
    id: "cu-shuttle",
    label: "CU Shuttle Bus (POP Bus) live",
    vendor: "Chulalongkorn University",
    category: "campus",
    status: "ready",
    endpoint: "https://www.cushuttlebus.com/api/live (TBD — confirm with CU IT)",
    apiPath: "/api/transit/cu-shuttle",
    keyEnv: "CU_SHUTTLE_TOKEN",
    pollSeconds: 30,
    describe: "5 free electric shuttle lines around campus; routes + stops shipped as GeoJSON, live positions need CU IT token.",
    docs: "https://www.chula.ac.th/about/green-university/cu-shuttle-bus/",
  },
  {
    id: "bts",
    label: "BTS Skytrain stations",
    vendor: "BTS Group",
    category: "mobility",
    status: "ready",
    apiPath: "/api/transit/bts",
    describe: "Static station GeoJSON (National Stadium, Siam, Ratchadamri are walking distance); arrivals require BTS API key.",
    docs: "https://www.bts.co.th/",
  },
  {
    id: "mrt",
    label: "MRT subway stations",
    vendor: "Bangkok Expressway and Metro",
    category: "mobility",
    status: "ready",
    apiPath: "/api/transit/mrt",
    describe: "Sam Yan, Hua Lamphong, Si Lom adjacent; static GeoJSON + GTFS for arrivals when feed key obtained.",
    docs: "https://metro.bemplc.co.th/",
  },
  {
    id: "bmta",
    label: "BMTA buses (GTFS)",
    vendor: "Bangkok Mass Transit Authority",
    category: "mobility",
    status: "planned",
    endpoint: "https://data.go.th/dataset/bmta-gtfs",
    describe: "Public GTFS feed via data.go.th; ingest stops within 800m of campus + route polylines.",
  },
  {
    id: "google-traffic",
    label: "Google Maps traffic raster",
    vendor: "Google",
    category: "mobility",
    status: "planned",
    keyEnv: "GOOGLE_MAPS_KEY",
    describe: "Tile overlay; commercial license required. Use as a fallback comparison to iTIC + simulated heatmap.",
  },
  {
    id: "tomtom-traffic",
    label: "TomTom traffic flow",
    vendor: "TomTom",
    category: "mobility",
    status: "planned",
    keyEnv: "TOMTOM_KEY",
    describe: "Free 2,500 req/day tier; per-segment speed vs. free-flow — good replacement for simulated heatmap.",
    docs: "https://developer.tomtom.com/traffic-api",
  },
  {
    id: "here-traffic",
    label: "HERE traffic flow + incidents",
    vendor: "HERE Technologies",
    category: "mobility",
    status: "planned",
    keyEnv: "HERE_API_KEY",
    describe: "Per-link jam factor JSON; free tier 250k req/month.",
    docs: "https://developer.here.com/",
  },

  // ---- Incidents ----
  {
    id: "traffy-fondue",
    label: "Traffy Fondue citizen reports",
    vendor: "NECTEC / BMA",
    category: "incidents",
    status: "live",
    endpoint: "https://publicapi.traffy.in.th/dump-csv-stat-bma/bangkok_traffy.json",
    apiPath: "/api/incidents/city-reports",
    pollSeconds: 300,
    describe: "Citizen complaint feed (potholes, flooding, waste, lighting); bbox-filtered to outer campus area.",
    docs: "https://www.traffy.in.th/",
  },
  {
    id: "city-reporter-v2",
    label: "City Reporter v2 (own intake)",
    vendor: "Dr Non — internal",
    category: "incidents",
    status: "planned",
    apiPath: "/api/incidents/internal",
    describe: "LINE/Telegram intake writing to Supabase; same shape as Traffy. Drop in when client wants their own channel.",
  },
  {
    id: "1555-bma",
    label: "BMA 1555 complaint line",
    vendor: "Bangkok Metropolitan Authority",
    category: "incidents",
    status: "research",
    describe: "Phone hotline; intake stats published as monthly PDF. No realtime API.",
  },

  // ---- Environment ----
  {
    id: "open-meteo",
    label: "Open-Meteo weather + forecast",
    vendor: "Open-Meteo",
    category: "environment",
    status: "live",
    endpoint: "https://api.open-meteo.com/v1/forecast",
    apiPath: "/api/weather",
    pollSeconds: 1800,
    describe: "No-key weather: current + 7d hourly. Used for KPI strip; also for rain prediction overlay later.",
  },
  {
    id: "open-meteo-aq",
    label: "Open-Meteo air quality grid",
    vendor: "Open-Meteo",
    category: "environment",
    status: "live",
    endpoint: "https://air-quality-api.open-meteo.com/v1/air-quality",
    apiPath: "/api/air-quality",
    pollSeconds: 900,
    describe: "US AQI + PM2.5/PM10 from Copernicus reanalysis grid. Free, no key.",
  },
  {
    id: "air4thai",
    label: "Air4Thai stations",
    vendor: "Pollution Control Department",
    category: "environment",
    status: "ready",
    endpoint: "http://air4thai.com/forweb/getAQI_JSON.php",
    apiPath: "/api/air-quality/stations",
    pollSeconds: 900,
    describe: "Official Thai AQI station data. Bangkok-10t (Klong Toei) and 53t (Wang Thong Lang) closest to campus.",
    docs: "http://air4thai.com/",
  },
  {
    id: "waqi",
    label: "WAQI (World AQI) tiles",
    vendor: "WAQI / EBAS",
    category: "environment",
    status: "planned",
    keyEnv: "WAQI_TOKEN",
    endpoint: "https://api.waqi.info/v2/feed/here/?token={token}",
    describe: "Crowd-sourced AQI with tile overlay; token from waqi.info.",
  },
  {
    id: "openaq",
    label: "OpenAQ historical",
    vendor: "OpenAQ",
    category: "environment",
    status: "planned",
    endpoint: "https://api.openaq.org/v2",
    describe: "Historical air-quality time series; useful for hour-of-day baselines, not realtime.",
  },
  {
    id: "tmd-weather",
    label: "TMD Thai Meteorological Dept.",
    vendor: "TMD",
    category: "environment",
    status: "planned",
    endpoint: "https://data.tmd.go.th/api/Weather3Hours/v1/",
    keyEnv: "TMD_KEY",
    describe: "Free Thai-language forecast with 3-hour granularity. Auth required (register).",
  },
  {
    id: "bma-flood",
    label: "BMA flood + drainage",
    vendor: "BMA Drainage Dept.",
    category: "environment",
    status: "planned",
    endpoint: "https://weather.bangkok.go.th/Radar/",
    describe: "BMA flood gates, pump stations, weather radar. Used by Smart City Monitor; port adapter.",
  },
  {
    id: "gistda-flood",
    label: "GISTDA flood extent",
    vendor: "GISTDA",
    category: "environment",
    status: "planned",
    endpoint: "https://flood.gistda.or.th/",
    describe: "Sentinel-1 SAR-derived flood polygons during Thai monsoon. WMS endpoint available.",
  },

  // ---- Imagery ----
  {
    id: "gibs-true-color",
    label: "NASA GIBS true-color (MODIS)",
    vendor: "NASA EOSDIS",
    category: "imagery",
    status: "live",
    endpoint: "https://gibs.earthdata.nasa.gov/wmts/epsg3857/best/MODIS_Terra_CorrectedReflectance_TrueColor/...",
    apiPath: "(client tile fetch)",
    describe: "1km true-color daily imagery; WMTS tile layer. Free, no key.",
    docs: "https://nasa-gibs.github.io/gibs-api-docs/",
  },
  {
    id: "gibs-ndvi",
    label: "NASA GIBS NDVI",
    vendor: "NASA EOSDIS",
    category: "imagery",
    status: "live",
    endpoint: "https://gibs.earthdata.nasa.gov/wmts/.../MODIS_Terra_NDVI_8Day/...",
    apiPath: "(client tile fetch)",
    describe: "Vegetation index 8-day composite. Useful for tracking campus greenery and heat-island gradient.",
  },
  {
    id: "sentinel-hub",
    label: "Sentinel Hub Process API",
    vendor: "ESA / Sinergise",
    category: "imagery",
    status: "planned",
    keyEnv: "SENTINEL_HUB_CLIENT_ID,SENTINEL_HUB_CLIENT_SECRET",
    endpoint: "https://services.sentinel-hub.com/api/v1/process",
    describe: "10m Sentinel-2 + Sentinel-1 SAR on demand. Free OAuth tier (30k PU/month).",
    docs: "https://docs.sentinel-hub.com/api/latest/",
  },
  {
    id: "gistda-theos",
    label: "GISTDA THEOS-2 / Pleiades",
    vendor: "GISTDA",
    category: "imagery",
    status: "planned",
    describe: "Thai high-res satellite; product order via dict.gistda.or.th, MOU needed for direct API.",
  },
  {
    id: "planetary-computer",
    label: "Microsoft Planetary Computer STAC",
    vendor: "Microsoft",
    category: "imagery",
    status: "planned",
    endpoint: "https://planetarycomputer.microsoft.com/api/stac/v1",
    describe: "STAC catalog + signed tile tokens; great for historical Sentinel/Landsat lookups.",
  },
  {
    id: "jaxa-himawari",
    label: "JAXA Himawari real-time",
    vendor: "JAXA / JMA",
    category: "imagery",
    status: "planned",
    endpoint: "https://www.eorc.jaxa.jp/ptree/index.html",
    describe: "Geostationary cloud loop, 10-min cadence. Use for storm approach.",
  },

  // ---- Vibes ----
  {
    id: "google-news-rss",
    label: "Google News RSS",
    vendor: "Google",
    category: "vibes",
    status: "live",
    endpoint: "https://news.google.com/rss/search?q=...",
    apiPath: "/api/news",
    pollSeconds: 300,
    describe: "Keyword-driven RSS (TH + EN) for Chula / Siam / Samyan / Henri Dunant.",
  },
  {
    id: "bangkok-post-rss",
    label: "Bangkok Post education RSS",
    vendor: "Bangkok Post",
    category: "vibes",
    status: "live",
    endpoint: "https://www.bangkokpost.com/rss/data/education.xml",
    apiPath: "/api/news",
    pollSeconds: 300,
    describe: "Higher-trust EN coverage; filtered to campus keywords inside our aggregator.",
  },
  {
    id: "thaipbs-rss",
    label: "Thai PBS English RSS",
    vendor: "Thai PBS",
    category: "vibes",
    status: "ready",
    endpoint: "https://www.thaipbsworld.com/feed/",
    describe: "Add to /api/news feed list.",
  },
  {
    id: "the-nation-rss",
    label: "The Nation RSS",
    vendor: "Nation Thailand",
    category: "vibes",
    status: "ready",
    endpoint: "https://www.nationthailand.com/rss",
    describe: "Add to /api/news feed list.",
  },
  {
    id: "talkwalker",
    label: "Talkwalker Free Alerts",
    vendor: "Talkwalker",
    category: "vibes",
    status: "planned",
    describe: "Email-driven alerts; route via webhook into news feed.",
  },
  {
    id: "reddit-rss",
    label: "Reddit r/Thailand + r/Bangkok",
    vendor: "Reddit",
    category: "vibes",
    status: "ready",
    endpoint: "https://www.reddit.com/r/Thailand+Bangkok/.rss",
    describe: "Public RSS; cap to keyword match around campus.",
  },
  {
    id: "google-trends",
    label: "Google Trends (campus terms)",
    vendor: "Google",
    category: "vibes",
    status: "planned",
    describe: "Daily/hourly trend deltas for 'Chula', 'Siam Square', 'Samyan'; needs serpapi or unofficial pytrends.",
  },

  // ---- BMA portals — POI + datasets ----
  {
    id: "bma-gis",
    label: "BMA GIS POIs (ArcGIS FeatureServer)",
    vendor: "Bangkok Metropolitan Authority",
    category: "infrastructure",
    status: "live",
    endpoint: "https://bmagis.bangkok.go.th/arcgis/rest/services",
    apiPath: "/api/bma/pois",
    pollSeconds: 3600,
    describe: "Hospitals, schools, fire/police stations, parks, markets, BMA offices, flood gates, pump stations, CCTV, bus stops — multi-layer ArcGIS, bbox-filtered.",
    docs: "https://bmagis.bangkok.go.th",
  },
  {
    id: "bma-data-portal",
    label: "BMA Open Data Portal (CKAN)",
    vendor: "Bangkok Metropolitan Authority",
    category: "infrastructure",
    status: "live",
    endpoint: "https://data.bangkok.go.th/api/3/action/package_search",
    apiPath: "/api/bma/datasets",
    pollSeconds: 21600,
    describe: "Keyword search across BMA-published datasets (hospitals, schools, fire stations, PM2.5, parks, waste, drainage, accidents).",
    docs: "https://data.bangkok.go.th/",
  },
  {
    id: "bma-citydata-portal",
    label: "BMA City Data Portal (CKAN, alt)",
    vendor: "Bangkok Metropolitan Authority",
    category: "infrastructure",
    status: "ready",
    endpoint: "https://citydataportal.bangkok.go.th/api/3/action/package_search",
    describe: "Secondary CKAN catalog (older portal). Some datasets only exist here; fall back when data.bangkok.go.th returns 0.",
  },
  {
    id: "pmcu-portfolio",
    label: "PMCU Property portfolio",
    vendor: "Property Management of Chulalongkorn University (PMCU)",
    category: "campus",
    status: "live",
    apiPath: "/geo/cu-lands.geojson",
    describe: "Full PMCU portfolio matched against OSM: Siamscape, Siam Square One, Samyan Mitrtown, Samyan Market, Chamchuri Square, MBK Center, Stadium One, Suphachalasai Stadium, Nimibutr Arena, Centenary Park, Thapthim Shrine, Chula Hospital. Under-construction Blocks 28/28X/33/34 are pending OSM authoring.",
    docs: "https://pmcu.co.th/",
  },
  {
    id: "pmcu-news",
    label: "PMCU announcements + projects",
    vendor: "Property Management of Chulalongkorn University (PMCU)",
    category: "campus",
    status: "research",
    endpoint: "https://pmcu.co.th/",
    describe: "Block 28X (Samyan young entrepreneurs), Block 33 (Suan Luang residential + wellness), Block 34 (planned), Samyan Smart City, Siam Square Walking Street. 1,153 rai (184 ha) total under management.",
    docs: "https://pmcu.co.th/",
  },
  {
    id: "cu-sustainability-kpis",
    label: "CU Sustainability KPIs",
    vendor: "Chulalongkorn University — Office of Sustainability",
    category: "campus",
    status: "research",
    endpoint: "https://sustainability.chula.ac.th/",
    describe: "Energy, water, waste, carbon, green-space KPIs published per development zone (Suan Luang–Sam Yan, Siam Square). Ingest as monthly time series.",
  },
  {
    id: "open-meteo-aq-trend",
    label: "Open-Meteo AQ 8h forecast (trend)",
    vendor: "Open-Meteo",
    category: "environment",
    status: "live",
    endpoint: "https://air-quality-api.open-meteo.com/v1/air-quality (hourly forecast)",
    apiPath: "/api/air-quality/trend",
    pollSeconds: 900,
    describe: "Drives the prominent AQI badge — current + next 8h US AQI + PM2.5 sparkline + advisory band.",
  },

  // ---- Infrastructure ----
  {
    id: "mea-outages",
    label: "MEA electricity outages",
    vendor: "Metropolitan Electricity Authority",
    category: "infrastructure",
    status: "planned",
    endpoint: "https://www.mea.or.th/profile/108",
    describe: "Scheduled outage notices; scrape into incident layer when timing intersects business hours.",
  },
  {
    id: "mwa-water",
    label: "MWA water outage notices",
    vendor: "Metropolitan Waterworks Authority",
    category: "infrastructure",
    status: "planned",
    endpoint: "https://www.mwa.co.th/article-detail/news-and-event/",
    describe: "Maintenance announcements affecting Pathum Wan district.",
  },
  {
    id: "cu-parking",
    label: "CU parking lot occupancy",
    vendor: "Chulalongkorn — Office of Property Management",
    category: "campus",
    status: "research",
    describe: "Smart parking pilot exists (sensors at some lots); need contact with OPM for data sharing.",
  },
  {
    id: "cu-energy",
    label: "CU green-university KPIs",
    vendor: "Chulalongkorn — Office of Sustainability",
    category: "campus",
    status: "research",
    endpoint: "https://www.chula.ac.th/about/green-university/",
    describe: "Energy, water, waste KPIs published in PDF; ingest as monthly time series.",
  },

  // ---- Global markets — FMP + FRED ----
  {
    id: "fmp-markets",
    label: "FMP — global indices, forex, commodities",
    vendor: "Financial Modeling Prep",
    category: "vibes",
    status: "live",
    endpoint: "https://financialmodelingprep.com/api/v3/quote/...",
    apiPath: "/api/markets",
    keyEnv: "FMP_API_KEY",
    pollSeconds: 600,
    describe: "Live ticks for SET Bangkok + global indices (S&P, Nikkei, Hang Seng, FTSE, DAX), THB forex pairs, and commodities (WTI, Brent, Gold, Silver). Free tier 250/day.",
    docs: "https://site.financialmodelingprep.com/developer/docs",
  },
  {
    id: "fred-macro",
    label: "FRED — US macro + Thai baht series",
    vendor: "Federal Reserve Economic Data (St. Louis Fed)",
    category: "vibes",
    status: "live",
    endpoint: "https://api.stlouisfed.org/fred/series/observations?...",
    apiPath: "/api/markets",
    keyEnv: "FRED_API_KEY",
    pollSeconds: 600,
    describe: "Daily series: DGS10 (US 10Y yield), DGS2 (2Y), VIXCLS (volatility), DEXTHUS (THB/USD), DCOILWTICO (WTI). 120k calls/day.",
    docs: "https://fred.stlouisfed.org/docs/api/fred/",
  },

  // ---- Added 2026-05-17 from Perplexity / Chula sustainability research ----
  {
    id: "aqicn-bangkok",
    label: "AQICN Bangkok stations (real-time)",
    vendor: "World Air Quality Index Project",
    category: "environment",
    status: "ready",
    endpoint: "https://api.waqi.info/feed/{station}/?token={AQICN_TOKEN}",
    keyEnv: "AQICN_TOKEN",
    pollSeconds: 900,
    describe: "Authoritative Bangkok AQ — PCD/BMA station network + US embassy monitors. Register free at aqicn.org/data-platform/token.",
    docs: "https://aqicn.org/api/",
  },
  {
    id: "air4thai",
    label: "Air4Thai — PCD national AQ network",
    vendor: "Pollution Control Department (PCD)",
    category: "environment",
    status: "ready",
    endpoint: "http://air4thai.pcd.go.th/webV2/wsAPI/getAQI.php",
    pollSeconds: 900,
    describe: "Official Thai government AQ stations including 50T (Chula Hospital area) and stations along Rama IV / Phaya Thai. No key required; rate-limited.",
    docs: "http://air4thai.pcd.go.th/webV2/",
  },
  {
    id: "chula-bems",
    label: "Chula BEMS — building energy data",
    vendor: "Chulalongkorn — Office of Sustainability",
    category: "infrastructure",
    status: "research",
    endpoint: "https://sustainability.chula.ac.th/report/610/",
    describe: "Building Energy Management System data published in the open-data list. Building-level kWh, demand, peaks — needs IT outreach for live feed.",
    docs: "https://sustainability.chula.ac.th/",
  },
  {
    id: "chula-zero-waste",
    label: "Chula Zero Waste — collection + diversion",
    vendor: "Chulalongkorn — Chula Zero Waste",
    category: "infrastructure",
    status: "research",
    endpoint: "https://sustainability.chula.ac.th/report/610/",
    describe: "Waste collection volumes by category, recycling diversion rates, organic + e-waste streams. Published as Zero Waste reports; ingest as monthly series.",
    docs: "https://www.chula.ac.th/en/news/zero-waste/",
  },
  {
    id: "bma-flood-platform",
    label: "BMA flood-risk platform (complaints + rainfall + drainage)",
    vendor: "Bangkok Metropolitan Administration",
    category: "environment",
    status: "research",
    endpoint: "https://www.bangkok.go.th/sub-portal/flood",
    describe: "Citizen flood complaints + rainfall gauges + drainage performance + canal levels fused in one BMA platform. Used by city ops; we need the underlying API/CKAN dataset names.",
    docs: "https://civicdatalab.in/work/climateaction/bma-flood-risk-data-platform/",
  },
  {
    id: "chula-buildings-dataset",
    label: "Chula building-recognition dataset (CV)",
    vendor: "Chulalongkorn — Faculty of Engineering",
    category: "campus",
    status: "research",
    endpoint: "https://arxiv.org/abs/2512.05468",
    describe: "Academic CV dataset for Chula campus buildings — for AR wayfinding overlays or auto-tagging photo evidence.",
  },
  {
    id: "data-go-th",
    label: "data.go.th — Thai government open data",
    vendor: "DGA (Digital Government Agency)",
    category: "infrastructure",
    status: "research",
    endpoint: "https://data.go.th/",
    describe: "National open-data portal — population, urban planning, transport, education. CKAN-compatible API; aggregator layer needed.",
    docs: "https://data.go.th/dataset",
  },
];

export function sourcesByStatus(status: SourceStatus) {
  return SOURCE_CATALOG.filter((s) => s.status === status);
}

export function sourcesByCategory(category: SourceCategory) {
  return SOURCE_CATALOG.filter((s) => s.category === category);
}
